import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAccessTiers } from '../../../hooks/usePremiumAccess';

const { FiX, FiLoader, FiUserPlus, FiShield } = FiIcons;

const inputCls = 'w-full glass-input rounded-xl px-4 py-3 text-sm focus:outline-none';

const GrantAccessModal = ({ artistId, drops = [], onClose }) => {
  const { tiers } = useAccessTiers(artistId);
  const [form, setForm] = useState({
    user_id: '',
    content_id: '',
    access_tier_id: '',
    amount_paid: '0',
    grant_source: 'manual',
    expires_at: '',
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGrant = async () => {
    if (!form.user_id.trim()) { setErr('User ID is required.'); return; }
    if (!form.content_id) { setErr('Select a drop to grant access to.'); return; }
    setLoading(true); setErr('');
    const { createClient } = await import('@supabase/supabase-js');
    const supabase = (await import('../../../supabase/supabase')).default;
    const { error } = await supabase.from('d2f_access_sessions').upsert({
      user_id: form.user_id.trim(),
      content_id: form.content_id,
      content_type: 'drop',
      access_tier_id: form.access_tier_id || null,
      amount_paid: parseFloat(form.amount_paid) || 0,
      grant_source: form.grant_source,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      revoked_at: null,
    }, { onConflict: 'user_id,content_id,content_type' });
    setLoading(false);
    if (error) { setErr(error.message); return; }
    setSuccess(true);
    setTimeout(() => { setSuccess(false); onClose(); }, 1500);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(20px)' }}>
      <div className="w-full max-w-md glass-strong rounded-3xl overflow-hidden"
        style={{ borderColor: 'rgba(100,140,255,0.15)', boxShadow: '0 0 60px rgba(26,58,255,0.1)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-7 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(100,140,255,0.12)', border: '1px solid rgba(100,140,255,0.2)' }}>
              <SafeIcon icon={FiUserPlus} className="text-base" style={{ color: 'rgba(150,180,255,0.9)' }} />
            </div>
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-white">Manual Grant</p>
              <p className="text-[10px] font-bold" style={{ color: 'rgba(255,255,255,0.3)' }}>Give a fan direct access</p>
            </div>
          </div>
          <button onClick={onClose} className="btn-glass p-2 rounded-xl text-white/40 hover:text-white/70">
            <SafeIcon icon={FiX} className="text-base" />
          </button>
        </div>

        {/* Body */}
        <div className="p-7 space-y-5">
          {success ? (
            <div className="text-center py-8 space-y-4">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-full"
                  style={{ background: 'radial-gradient(circle, rgba(52,211,153,0.15), transparent 70%)' }} />
                <div className="w-20 h-20 rounded-full flex items-center justify-center relative"
                  style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <SafeIcon icon={FiShield} className="text-4xl text-emerald-400" />
                </div>
              </div>
              <p className="font-black text-white text-xl uppercase tracking-tight">Access Granted!</p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Fan User ID *</label>
                <input className={inputCls} placeholder="uuid..." value={form.user_id} onChange={e => set('user_id', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Select Drop *</label>
                <select className={inputCls} value={form.content_id} onChange={e => set('content_id', e.target.value)}>
                  <option value="">— Choose drop —</option>
                  {drops.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Access Tier</label>
                  <select className={inputCls} value={form.access_tier_id} onChange={e => set('access_tier_id', e.target.value)}>
                    <option value="">— None —</option>
                    {tiers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Source</label>
                  <select className={inputCls} value={form.grant_source} onChange={e => set('grant_source', e.target.value)}>
                    <option value="manual">Manual</option>
                    <option value="promo">Promo</option>
                    <option value="pwyw">PWYW</option>
                    <option value="subscription">Subscription</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Amount Paid</label>
                  <input type="number" min="0" step="0.01" className={inputCls} value={form.amount_paid} onChange={e => set('amount_paid', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-white/35">Expires At</label>
                  <input type="datetime-local" className={inputCls} value={form.expires_at} onChange={e => set('expires_at', e.target.value)} />
                </div>
              </div>
              {err && (
                <p className="text-xs font-black" style={{ color: 'rgba(248,113,113,0.9)' }}>{err}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 px-7 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <button onClick={onClose} className="btn-glass px-5 py-3 rounded-2xl text-white/50">Cancel</button>
            <button onClick={handleGrant} disabled={loading}
              className="btn-cobalt flex items-center gap-2 px-6 py-3 rounded-2xl disabled:opacity-50">
              {loading && <SafeIcon icon={FiLoader} className="animate-spin text-sm" />}
              Grant Access
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrantAccessModal;